import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Menu,
  useTheme,
  alpha,
} from "@mui/material";
import MenuBookIcon from "@mui/icons-material/MenuBookRounded";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import {
  useGetStaffTeachingProfileQuery,
  useGetClassScheduleFilterOptionsQuery,
  useAddStaffTeachingSubjectMutation,
} from "../../../../Api/attoDeskApi";
import { SECTION_IDS, invertGradeSubjectsToSubjects } from "../staffProfileView";
import useUser from "../../../../Core/hook/useUser";
import { Privileges } from "../../../../Core/Enum/enum";
import { useNotifier } from "../../../../Core/Notifier";
import { normalizeSubjectOptions } from "../../../ClassSchedule/List/filterOptions";

const SUBJECT_PALETTE = ["#6366f1", "#0ea5e9", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6"];

// ── Add Subject Dialog ──────────────────────────────────────────────────────
type AddSubjectDialogMode = "add-subject" | "add-grades";

interface AddSubjectDialogProps {
  open: boolean;
  onClose: () => void;
  staffId: number | string;
  staffName?: string;
  assignedBySubject: Map<number, Set<number>>;
  onAdded: (subjectId: number) => void;
  mode?: AddSubjectDialogMode;
  initialSubjectId?: number | null;
  initialSubjectName?: string | null;
}

const AddSubjectDialog: React.FC<AddSubjectDialogProps> = ({
  open,
  onClose,
  staffId,
  staffName,
  assignedBySubject,
  onAdded,
  mode = "add-subject",
  initialSubjectId = null,
  initialSubjectName = null,
}) => {
  const { showMessage, showErrorMessage } = useNotifier();
  const { data: filterOptionsData } = useGetClassScheduleFilterOptionsQuery();
  const allSubjects = useMemo(
    () => normalizeSubjectOptions((filterOptionsData as any)?.data),
    [filterOptionsData]
  );

  const isAddGrades = mode === "add-grades" && !!initialSubjectId;
  const [subjectId, setSubjectId] = useState<number | "">("");
  const [gradeIds, setGradeIds] = useState<number[]>([]);
  const [addSubject, { isLoading }] = useAddStaffTeachingSubjectMutation();

  useEffect(() => {
    if (!open) {
      setSubjectId("");
      setGradeIds([]);
      return;
    }
    setGradeIds([]);
    setSubjectId(isAddGrades ? Number(initialSubjectId) : "");
  }, [open, isAddGrades, initialSubjectId]);

  const availableSubjects = useMemo(() => allSubjects.filter((s) => {
    const assigned = assignedBySubject.get(s.id) || new Set<number>();
    return s.grade_ids.some((g) => !assigned.has(g));
  }), [allSubjects, assignedBySubject]);

  const selectedSubject = allSubjects.find((s) => s.id === Number(subjectId));
  const lockedSubjectName =
    isAddGrades
      ? (selectedSubject?.name || initialSubjectName || "")
      : "";
  const gradeOptions = useMemo(() => {
    if (!selectedSubject) return [] as Array<{ id: number; name: string; assigned: boolean }>;
    const assigned = assignedBySubject.get(selectedSubject.id) || new Set<number>();
    const gradeNameMap = new Map<number, string>();
    const grades = ((filterOptionsData as any)?.data?.grades || []) as any[];
    grades.forEach((g: any) => gradeNameMap.set(Number(g.id), g.name || g.grade || `Grade #${g.id}`));
    return selectedSubject.grade_ids
      .map((gid) => ({ id: gid, name: gradeNameMap.get(gid) || `Grade #${gid}`, assigned: assigned.has(gid) }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [selectedSubject, assignedBySubject, filterOptionsData]);

  useEffect(() => {
    if (!selectedSubject) { setGradeIds([]); return; }
    const valid = new Set(selectedSubject.grade_ids);
    setGradeIds((prev) => prev.filter((g) => valid.has(g)));
  }, [selectedSubject]);

  const selectableGrades = gradeOptions.filter((g) => !g.assigned);
  const canSave = !!subjectId && gradeIds.length > 0 && !isLoading;

  const handleSelectAllAvailable = () => setGradeIds(selectableGrades.map((g) => g.id));
  const handleClearGrades = () => setGradeIds([]);

  const handleSave = async () => {
    if (!canSave) return;
    try {
      const res = await addSubject({
        staff_id: staffId,
        subject_id: Number(subjectId),
        grade_ids: gradeIds,
      }).unwrap();
      if (res?.status) {
        showMessage(res.message || "Teaching subject added");
        onAdded(Number(subjectId));
        onClose();
      } else {
        showErrorMessage(res?.message || "Failed to add teaching subject");
      }
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Failed to add teaching subject");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm"
      PaperProps={{ sx: { borderRadius: "14px" } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
        <Box>
          <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
            {isAddGrades
              ? `Add Grades to ${lockedSubjectName || "Subject"}`
              : "Add Teaching Subject"}
          </Typography>
          {staffName && <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{staffName}</Typography>}
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          {isAddGrades ? (
            <FormControl fullWidth size="small" disabled>
              <InputLabel shrink>Subject</InputLabel>
              <Select
                label="Subject"
                notched
                value={Number(initialSubjectId)}
                renderValue={() => (
                  <Typography sx={{ fontSize: 13 }}>{lockedSubjectName}</Typography>
                )}
              >
                <MenuItem value={Number(initialSubjectId)}>{lockedSubjectName}</MenuItem>
              </Select>
              <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.5 }}>
                Adding grades to {lockedSubjectName}
              </Typography>
            </FormControl>
          ) : (
            <FormControl fullWidth size="small">
              {/* `shrink` + `notched` keeps the floating label anchored above
                  the outline instead of overlapping the placeholder text when
                  the field is empty (which was the source of the messy
                  overlap on the /team-history dialog). */}
              <InputLabel shrink>Subject</InputLabel>
              <Select
                label="Subject"
                notched
                displayEmpty
                value={subjectId}
                onChange={(e) => setSubjectId(Number(e.target.value) || "")}
                renderValue={(selected) => {
                  const id = Number(selected);
                  if (!id) {
                    return (
                      <Typography sx={{ fontSize: 13, color: "text.disabled" }}>
                        Select a subject
                      </Typography>
                    );
                  }
                  const name = availableSubjects.find((s) => s.id === id)?.name;
                  return <Typography sx={{ fontSize: 13 }}>{name || ""}</Typography>;
                }}
              >
                {availableSubjects.map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))}
                {availableSubjects.length === 0 && (
                  <MenuItem disabled>
                    <Typography sx={{ fontSize: 12.5, color: "text.disabled" }}>
                      All active subjects are already fully assigned.
                    </Typography>
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          )}

          <FormControl fullWidth size="small" disabled={!subjectId}>
            <InputLabel shrink>Grades</InputLabel>
            <Select
              multiple
              notched
              displayEmpty
              label="Grades"
              value={gradeIds}
              onChange={(e) => {
                const v = e.target.value;
                setGradeIds(typeof v === "string" ? [] : (v as number[]));
              }}
              renderValue={(selected) => {
                if (!subjectId) {
                  return (
                    <Typography sx={{ fontSize: 13, color: "text.disabled" }}>
                      {isAddGrades ? "Loading grades…" : "Select a subject first"}
                    </Typography>
                  );
                }
                const ids = selected as number[];
                if (ids.length === 0) return <Typography sx={{ fontSize: 13, color: "text.disabled" }}>Select one or more grades</Typography>;
                const names = ids
                  .map((id) => gradeOptions.find((g) => g.id === id)?.name)
                  .filter(Boolean) as string[];
                return names.join(", ");
              }}
            >
              {gradeOptions.length === 0 && subjectId && (
                <MenuItem disabled>
                  <Typography sx={{ fontSize: 12.5, color: "text.disabled" }}>
                    No grades are configured for this subject.
                  </Typography>
                </MenuItem>
              )}
              {gradeOptions.map((g) => (
                <MenuItem key={g.id} value={g.id} disabled={g.assigned}>
                  <Checkbox size="small" checked={gradeIds.includes(g.id)} disabled={g.assigned} sx={{ py: 0.25, mr: 0.5 }} />
                  <ListItemText
                    primary={g.name}
                    secondary={g.assigned ? "Already assigned" : undefined}
                    primaryTypographyProps={{ fontSize: 13 }}
                    secondaryTypographyProps={{ fontSize: 11, color: "text.disabled" }}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {isAddGrades && subjectId && gradeOptions.length > 0 && selectableGrades.length === 0 && (
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              All configured grades for {lockedSubjectName} are already assigned{staffName ? ` to ${staffName}` : ""}.
            </Typography>
          )}

          {subjectId && selectableGrades.length > 0 && (
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: -0.5 }}>
              <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
                {gradeIds.length} of {selectableGrades.length} available selected
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button size="small" onClick={handleSelectAllAvailable} sx={{ textTransform: "none", fontSize: 11.5 }}>Select All Available</Button>
                <Button size="small" onClick={handleClearGrades} sx={{ textTransform: "none", fontSize: 11.5 }} disabled={gradeIds.length === 0}>Clear</Button>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none", fontWeight: 600 }}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave}
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px" }}>
          {isLoading ? <CircularProgress size={16} sx={{ mr: 1, color: "inherit" }} /> : null}
          {isAddGrades
            ? gradeIds.length > 1
              ? `Add ${gradeIds.length} Grade Assignments`
              : "Add Grade Assignment"
            : "Add Subject Assignment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Subject row ── one compact row per subject, no expansion, no class content
interface CapabilitySubject {
  id: number;
  name: string;
  grades: Array<{ id: number; name: string }>;
}

const SubjectRow: React.FC<{
  subject: CapabilitySubject;
  color: string;
  canEdit: boolean;
  onAddGrades: () => void;
}> = ({ subject, color, canEdit, onAddGrades }) => {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const openMenu = (e: React.MouseEvent<HTMLElement>) => setAnchor(e.currentTarget);
  const closeMenu = () => setAnchor(null);
  return (
    <Box
      data-testid="teaching-subject-row"
      sx={{
        bgcolor: "#fff",
        borderRadius: "14px",
        border: "1px solid rgba(0,0,0,0.06)",
        minHeight: 64,
        px: 2, py: 1.25,
        display: "flex",
        alignItems: { xs: "flex-start", sm: "center" },
        gap: 1.5,
        borderLeft: `3px solid ${color}`,
        flexDirection: { xs: "column", sm: "row" },
        transition: "box-shadow 150ms ease",
        "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.04)" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0, flex: { sm: "0 0 auto" }, width: { xs: "100%", sm: "auto" } }}>
        <Box sx={{ width: 34, height: 34, borderRadius: "9px", bgcolor: alpha(color, 0.12), color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <MenuBookIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.25 }}>{subject.name}</Typography>
          <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 0.15 }}>
            {subject.grades.length} {subject.grades.length === 1 ? "grade" : "grades"}
          </Typography>
        </Box>
      </Box>

      <Box sx={{
        flex: 1, minWidth: 0,
        display: "flex", flexWrap: "wrap", gap: 0.75,
        justifyContent: { xs: "flex-start", sm: "flex-start" },
        pl: { xs: 0, sm: 1 },
      }}>
        {subject.grades.map((g) => (
          <Chip
            key={g.id}
            label={g.name}
            size="small"
            sx={{
              height: 24,
              fontSize: 11.5,
              fontWeight: 600,
              bgcolor: alpha(color, 0.10),
              color,
              border: `1px solid ${alpha(color, 0.25)}`,
            }}
          />
        ))}
      </Box>

      {canEdit && (
        <>
          <IconButton
            size="small"
            aria-label="Subject actions"
            onClick={openMenu}
            sx={{ alignSelf: { xs: "flex-end", sm: "center" } }}
          >
            <MoreHorizIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={closeMenu}>
            <MenuItem onClick={() => { closeMenu(); onAddGrades(); }} sx={{ fontSize: 13 }}>
              Add Grades
            </MenuItem>
          </Menu>
        </>
      )}
    </Box>
  );
};

// ── Teaching Assignments Section ─────────────────────────────────────────────
const TeachingProfileSection: React.FC<{
  staffId: string;
  staffName?: string;
}> = ({ staffId, staffName }) => {
  const theme = useTheme();
  const { userHasRight } = useUser();
  const canEdit = userHasRight(Privileges.ClassesEdit);
  const { data, isFetching } = useGetStaffTeachingProfileQuery({ staffId });
  const payload = (data as any)?.data;
  const gradeSubjects: any[] = payload?.grade_subjects ?? [];

  // Build subject-first capability list from the grade → subjects mapping the
  // backend already merges (class templates + staff_grades_subject_tables
  // pivot). This section is capability-only — never derived from active classes
  // alone, so a qualified teacher shows up even with no scheduled classes.
  const capabilitySubjects = useMemo<CapabilitySubject[]>(
    () => invertGradeSubjectsToSubjects(gradeSubjects),
    [gradeSubjects]
  );

  const gradeSubjectAssignments = capabilitySubjects.reduce(
    (sum, s) => sum + s.grades.length,
    0,
  );
  const subjectCount = capabilitySubjects.length;

  const [addOpen, setAddOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<AddSubjectDialogMode>("add-subject");
  const [targetSubject, setTargetSubject] = useState<{ id: number; name: string } | null>(null);

  const openAddSubject = () => {
    setDialogMode("add-subject");
    setTargetSubject(null);
    setAddOpen(true);
  };
  const openAddGrades = (subject: CapabilitySubject) => {
    setDialogMode("add-grades");
    setTargetSubject({ id: subject.id, name: subject.name });
    setAddOpen(true);
  };

  const assignedBySubject = useMemo(() => {
    const map = new Map<number, Set<number>>();
    capabilitySubjects.forEach((s) => {
      map.set(s.id, new Set(s.grades.map((g) => g.id)));
    });
    return map;
  }, [capabilitySubjects]);

  if (isFetching && !payload) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}><CircularProgress size={24} /></Box>;
  }

  return (
    <Box>
      <Box id={SECTION_IDS.assignments} sx={{ scrollMarginTop: 80 }}>
        <Box sx={{
          display: "flex", alignItems: { xs: "flex-start", sm: "center" }, justifyContent: "space-between",
          gap: 1.5, mb: 1.5, flexDirection: { xs: "column", sm: "row" },
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 34, height: 34, borderRadius: "10px", bgcolor: alpha(theme.palette.primary.main, 0.08), display: "flex", alignItems: "center", justifyContent: "center" }}>
              <MenuBookIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 15, fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.2 }}>Teaching Assignments</Typography>
              <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 0.1 }} data-testid="teaching-summary">
                {subjectCount} {subjectCount === 1 ? "Subject" : "Subjects"} · {gradeSubjectAssignments} Grade–Subject {gradeSubjectAssignments === 1 ? "Assignment" : "Assignments"}
              </Typography>
            </Box>
          </Box>
          {canEdit && (
            <Button
              startIcon={<AddIcon sx={{ fontSize: 18 }} />}
              variant="contained"
              size="small"
              onClick={openAddSubject}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "10px",
                px: 2,
                width: { xs: "100%", sm: "auto" },
              }}
            >
              Add Subject
            </Button>
          )}
        </Box>

        {capabilitySubjects.length === 0 ? (
          <Box sx={{ bgcolor: "#fff", borderRadius: "14px", border: "1px solid rgba(0,0,0,0.06)", textAlign: "center", py: 5, px: 3 }}>
            <AutoStoriesOutlinedIcon sx={{ fontSize: 36, color: "rgba(0,0,0,0.08)", mb: 1.5 }} />
            <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 0.5 }}>No teaching subjects assigned</Typography>
            <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 2 }}>
              Add the grades and subjects this staff member is qualified to teach.
            </Typography>
            {canEdit && (
              <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={openAddSubject}
                sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px" }}>
                Add Subject
              </Button>
            )}
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {capabilitySubjects.map((subj, si) => (
              <SubjectRow
                key={subj.id}
                subject={subj}
                color={SUBJECT_PALETTE[si % SUBJECT_PALETTE.length]}
                canEdit={canEdit}
                onAddGrades={() => openAddGrades(subj)}
              />
            ))}
          </Box>
        )}
      </Box>

      <AddSubjectDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        staffId={staffId}
        staffName={staffName}
        assignedBySubject={assignedBySubject}
        onAdded={() => {}}
        mode={dialogMode}
        initialSubjectId={targetSubject?.id ?? null}
        initialSubjectName={targetSubject?.name ?? null}
      />
    </Box>
  );
};

export default TeachingProfileSection;
